import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { ScratchCard } from '@/components/ScratchCard/ScratchCard'
import { ApiClientError } from '@/lib/api/client'
import type { AchievementRewards } from '@/lib/campaign-gamification'
import {
  playCampaignScratchCard,
  revealCampaignScratchCard,
  type ScratchCardAttempt,
  type ScratchCardState,
} from '@/lib/campaign-scratch-card'
import styles from './LandingScratchCardModal.module.css'

export function LandingScratchCardModal({
  campaignId,
  state,
  rewards,
  onClose,
  onViewPrizes,
}: {
  campaignId: number
  state?: ScratchCardState
  rewards?: AchievementRewards
  onClose: (changed: boolean) => void
  onViewPrizes: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [attempt, setAttempt] = useState<ScratchCardAttempt | null>(
    state?.attempt ?? null
  )
  const [revealed, setRevealed] = useState(Boolean(state?.attempt?.revealed_at))
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [error, setError] = useState('')
  const changed = useRef(false)

  useEffect(() => {
    const element = dialog.current
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    element?.showModal()
    return () => {
      element?.close()
      previousFocus?.focus()
    }
  }, [])

  async function act(action: 'play' | 'reveal') {
    if (busyRef.current) return
    busyRef.current = true
    setBusy(true)
    setError('')
    try {
      const result = await (action === 'play'
        ? playCampaignScratchCard(campaignId)
        : revealCampaignScratchCard(campaignId))
      setAttempt(result)
      changed.current = true
      if (result.revealed_at) setRevealed(true)
    } catch (cause) {
      setError(
        cause instanceof ApiClientError
          ? (Object.values(cause.errors ?? {}).flat()[0] ?? cause.message)
          : 'Não foi possível concluir agora. Tente novamente; sua chance fica guardada.'
      )
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  const prize = attempt?.prize
  const isWinner = revealed && attempt?.won
  const close = () => {
    if (!busyRef.current) onClose(changed.current)
  }

  return (
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="scratch-card-title"
      onCancel={(event) => {
        event.preventDefault()
        close()
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) close()
      }}
    >
      <div className={styles.body}>
        <button
          type="button"
          className={styles.close}
          aria-label="Fechar raspadinha"
          onClick={close}
          disabled={busy}
        >
          <Icon icon="mdi:close" />
        </button>
        {isWinner && (
          <div className={styles.sparkles} aria-hidden>
            <i>✦</i>
            <i>✧</i>
            <i>✦</i>
            <i>✧</i>
            <i>✦</i>
            <i>✧</i>
          </div>
        )}
        <p className={styles.kicker}>
          {revealed
            ? attempt?.won
              ? 'Sua sorte brilhou!'
              : 'Obrigado por participar'
            : 'Selo 21 · Um toque de sorte'}
        </p>
        <img
          className={`${styles.badge} ${isWinner ? styles.winner : ''}`}
          src="/conquistas%20doa%C3%A7%C3%B5es/21%20Raspadinha.png"
          alt=""
        />
        <h2 id="scratch-card-title">
          {revealed ? attempt?.title : 'Raspe. Revele. Surpreenda-se.'}
        </h2>
        <p className={styles.intro}>
          {revealed
            ? attempt?.text ||
              'Que bom ter você por aqui. Sua surpresa já está guardada na sua conta!'
            : state?.invitation ||
              'Sua próxima surpresa pode estar a um toque de distância.'}
        </p>
        {rewards &&
        (rewards.coupon_percent || rewards.lucky_numbers || rewards.roulette_spins) ? (
          <div className={styles.receipt}>
            <strong>Extras por conquistar este selo ao revelar o cartão</strong>
            <ul>
              {rewards.coupon_percent ? (
                <li>Cupom de {rewards.coupon_percent}% para a próxima compra.</li>
              ) : null}
              {rewards.lucky_numbers > 0 ? (
                <li>
                  {rewards.lucky_numbers} números extras para resgatar na aba Números.
                </li>
              ) : null}
              {rewards.roulette_spins > 0 ? (
                <li>
                  {rewards.roulette_spins} giros extras. Compartilhe a campanha para
                  liberar a roleta.
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
        {!attempt ? (
          <>
            <div className={styles.teaser} aria-hidden>
              <Icon icon="mdi:gesture-swipe" />
              <span>Uma chance. Uma surpresa.</span>
              <small>O que será que vem por aí?</small>
            </div>
            <p className={styles.rule}>
              {state?.message || 'A raspadinha ainda não está disponível nesta campanha.'}
            </p>
            <button
              type="button"
              className={styles.action}
              disabled={busy || !state?.available}
              onClick={() => void act('play')}
            >
              <Icon icon={busy ? 'mdi:loading' : 'mdi:star-four-points'} aria-hidden />{' '}
              {busy ? 'Preparando sua surpresa…' : 'Quero raspar!'}
            </button>
            <small className={styles.footnote}>
              Grátis · Uma tentativa por campanha · Pode não haver prêmio
            </small>
          </>
        ) : (
          <>
            <ScratchCard
              initiallyRevealed={Boolean(attempt.revealed_at)}
              onReveal={() => {
                setRevealed(true)
                void act('reveal')
              }}
            >
              <Icon
                className={styles.resultIcon}
                icon={
                  prize?.prize_type === 'lucky_number'
                    ? 'mdi:ticket-confirmation-outline'
                    : prize?.prize_type === 'coupon'
                      ? 'mdi:ticket-percent-outline'
                      : 'mdi:heart-outline'
                }
                aria-hidden
              />
              <strong className={styles.resultValue}>
                {prize?.prize_type === 'lucky_number'
                  ? `+${prize.prize_payload?.quantity}`
                  : prize?.prize_type === 'coupon'
                    ? `${prize.prize_payload?.discount_percent}%`
                    : 'Seu apoio vale muito'}
              </strong>
              <span className={styles.resultLabel}>
                {prize?.prize_type === 'lucky_number'
                  ? 'números da sorte extras'
                  : prize?.prize_type === 'coupon'
                    ? 'de desconto na próxima compra'
                    : 'Hoje não veio um prêmio'}
              </span>
            </ScratchCard>
            {revealed && (
              <div className={styles.revealContent} aria-live="polite">
                <p className={styles.receipt}>
                  {prize?.prize_type === 'lucky_number'
                    ? prize.status === 'used'
                      ? 'Seus números já foram resgatados e estão ativos.'
                      : 'Seus números estão reservados. Vá à aba Números e toque em Resgatar para ativá-los.'
                    : prize?.prize_type === 'coupon'
                      ? prize.status === 'used'
                        ? 'Você já utilizou este cupom.'
                        : 'Cupom guardado na sua conta. Use na próxima compra desta campanha.'
                      : 'Sua participação ficou registrada. Obrigado por fazer parte desta campanha!'}
                </p>
                {prize?.prize_type === 'coupon' && (
                  <code className={styles.coupon}>{prize.prize_payload?.code}</code>
                )}
                {attempt.video_url &&
                  /^https:\/\/www\.youtube-nocookie\.com\/embed\/[A-Za-z0-9_-]{11}$/.test(
                    attempt.video_url
                  ) && (
                    <iframe
                      className={styles.video}
                      src={attempt.video_url}
                      title="Mensagem da sua conquista"
                      sandbox="allow-scripts allow-same-origin"
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  )}
                {error && !attempt.revealed_at ? (
                  <button
                    type="button"
                    className={styles.action}
                    disabled={busy}
                    onClick={() => void act('reveal')}
                  >
                    Tentar salvar revelação novamente
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.action}
                    disabled={busy}
                    onClick={() => {
                      close()
                      if (attempt.won) onViewPrizes()
                    }}
                  >
                    {busy
                      ? 'Guardando sua conquista…'
                      : attempt.won
                        ? 'Ver meus prêmios'
                        : 'Continuar minha jornada'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </dialog>
  )
}
